import { useNavigate } from "react-router-dom";

import { APP_ROUTES } from "./appRoutes";
import { useLoginUser } from "../hooks/useLoginUser";

type NavigateOptions = {
  replace?: boolean;
  state?: unknown;
};

export const useAppNavigate = () => {
  const navigate = useNavigate();
  const { isSystemAdmin } = useLoginUser();

  return {
    meNavigate: {
      detail: (options?: NavigateOptions) =>
        navigate(APP_ROUTES.me.detail.path, options),
      update: (options?: NavigateOptions) =>
        navigate(APP_ROUTES.me.update.path, options),
      setPassword: (options?: NavigateOptions) =>
        navigate(APP_ROUTES.me.setPassword.path, options),
      setCurrentBuilding: (options?: NavigateOptions) =>
        navigate(APP_ROUTES.me.setCurrentBuilding.path, options),
    },
    publicNavigate: {
      login: (options?: NavigateOptions) =>
        navigate(APP_ROUTES.public.login.path, options),
      passwordResetReqset: (options?: NavigateOptions) =>
        navigate(APP_ROUTES.public.passwordResetRequest.path, options),
      passwordResetVerify: (options?: NavigateOptions) =>
        navigate(APP_ROUTES.public.passwordResetVerify.path, options),
      passwordSet: (options?: NavigateOptions) =>
        navigate(APP_ROUTES.public.passwordSet.path, options),
    },
    authenticatedNavigate: {
      root: (options?: NavigateOptions) =>
        navigate(APP_ROUTES.authenticated.root.path, options),
    },
    systemAdminNavigate: {
      createSystemAdmin: (options?: NavigateOptions) => {
        navigate(APP_ROUTES.systemAdminOnly.createSystemAdmin.path, options);
      },
      listSystemAdmin: (options?: NavigateOptions) => {
        navigate(APP_ROUTES.systemAdminOnly.listSystemAdmin.path, options);
      },
      detailSystemAdmin: (id: string, options?: NavigateOptions) => {
        navigate(
          APP_ROUTES.systemAdminOnly.detailSystemAdmin.route(id),
          options
        );
      },
      updateSystemAdmin: (id: string, options?: NavigateOptions) => {
        navigate(
          APP_ROUTES.systemAdminOnly.updateSystemAdmin.route(id),
          options
        );
      },
      listTenants: (options?: NavigateOptions) => {
        navigate(APP_ROUTES.systemAdminOnly.listTenants.path, options);
      },
      createTenant: (options?: NavigateOptions) => {
        navigate(APP_ROUTES.systemAdminOnly.createTenant.path, options);
      },
      updateTenant: (tenantId: string, options?: NavigateOptions) => {
        navigate(
          APP_ROUTES.systemAdminOnly.updateTenant.route(tenantId),
          options
        );
      },
      listRobot: (options?: NavigateOptions) => {
        navigate(APP_ROUTES.systemAdminOnly.listRobot.path, options);
      },
      detailRobot: (robotId: string, options?: NavigateOptions) => {
        navigate(
          APP_ROUTES.systemAdminOnly.detailRobot.route(robotId),
          options
        );
      },
      updateRobot: (robotId: string, options?: NavigateOptions) => {
        navigate(
          APP_ROUTES.systemAdminOnly.updateRobot.route(robotId),
          options
        );
      },
    },
    systemOrTenantAdminNavigate: {
      detailTenant: (tenantId?: string, options?: NavigateOptions) => {
        if (isSystemAdmin) {
          if (!tenantId) return;
          navigate(
            APP_ROUTES.systemAdminOnly.detailTenant.route(tenantId),
            options
          );
          return;
        }
        navigate(APP_ROUTES.tenantAdminOnly.detailTenant.path, options);
      },
      listTenantUser: (tenantId?: string, options?: NavigateOptions) => {
        if (isSystemAdmin) {
          if (!tenantId) return;
          navigate(
            APP_ROUTES.systemAdminOnly.listTenantUser.route(tenantId),
            options
          );
          return;
        }
        navigate(APP_ROUTES.tenantAdminOnly.listTenantUser.path, options);
      },
      detailTenantUser: (
        { tenantId, userId }: { tenantId?: string; userId: string },
        options?: NavigateOptions
      ) => {
        if (isSystemAdmin) {
          if (!tenantId) return;
          navigate(
            APP_ROUTES.systemAdminOnly.detailTenantUser.route(tenantId, userId),
            options
          );
          return;
        }
        navigate(
          APP_ROUTES.tenantAdminOnly.detailTenantUser.route(userId),
          options
        );
      },
      updateTenantUser: (
        { tenantId, userId }: { tenantId?: string; userId: string },
        options?: NavigateOptions
      ) => {
        if (isSystemAdmin) {
          if (!tenantId) return;
          navigate(
            APP_ROUTES.systemAdminOnly.updateTenantUser.route(tenantId, userId),
            options
          );
          return;
        }
        navigate(
          APP_ROUTES.tenantAdminOnly.updateTenantUser.route(userId),
          options
        );
      },
      createTenantUser: (tenantId?: string, options?: NavigateOptions) => {
        if (isSystemAdmin) {
          if (!tenantId) return;
          navigate(
            APP_ROUTES.systemAdminOnly.createTenantUser.route(tenantId),
            options
          );
          return;
        }
        navigate(APP_ROUTES.tenantAdminOnly.createTenantUser.path, options);
      },
      listTenantRobot: (tenantId?: string, options?: NavigateOptions) => {
        if (isSystemAdmin) {
          if (!tenantId) return;
          navigate(
            APP_ROUTES.systemAdminOnly.listTenantRobot.route(tenantId),
            options
          );
          return;
        }
        navigate(APP_ROUTES.tenantAdminOnly.listTenantRobot.path, options);
      },
      detailTenantRobot: (
        robotId: string,
        tenantId?: string,
        options?: NavigateOptions
      ) => {
        if (isSystemAdmin) {
          if (!tenantId) return;
          navigate(
            APP_ROUTES.systemAdminOnly.detailTenantRobot.route(
              tenantId,
              robotId
            ),
            options
          );
          return;
        }
        navigate(
          APP_ROUTES.tenantAdminOnly.detailTenantRobot.route(robotId),
          options
        );
      },
      updateTenantRobot: (
        robotId: string,
        tenantId?: string,
        options?: NavigateOptions
      ) => {
        if (isSystemAdmin) {
          if (!tenantId) return;
          navigate(
            APP_ROUTES.systemAdminOnly.updateTenantRobot.route(
              tenantId,
              robotId
            ),
            options
          );
          return;
        }
        navigate(
          APP_ROUTES.tenantAdminOnly.updateTenantRobot.route(robotId),
          options
        );
      },
    },

    backNavigate: () => navigate(-1),
  };
};
